import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageSwitcherProps {
  alignUp?: boolean;
}

export default function LanguageSwitcher({ alignUp = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isRtl = i18n.dir() === "rtl";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizeLanguage = (lng: string) => lng.split("-")[0];

  const getCountryCode = (lng: string): string => {
    const code = normalizeLanguage(lng).toLowerCase();
    if (code === "ar") return "sa";
    if (code === "en") return "us";
    return code;
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(normalizeLanguage(lng));
    setOpen(false);
  };

  const languages = Object.keys(i18n.options.resources || {});
  const currentLang = normalizeLanguage(i18n.language || "ar");
  const currentLangLabel = i18n.getFixedT(currentLang)("languageName", { defaultValue: currentLang });

  return (
    <div className="relative z-[60]" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors whitespace-nowrap"
      >
        <img
          src={`https://flagcdn.com/w40/${getCountryCode(currentLang)}.png`}
          srcSet={`https://flagcdn.com/w80/${getCountryCode(currentLang)}.png 2x`}
          width="20"
          className="rounded-sm object-cover shadow-sm flex-shrink-0"
          alt={currentLang}
        />
        <span className="hidden sm:inline">{currentLangLabel}</span>
        <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute ${
            alignUp ? "bottom-full mb-2" : "top-full mt-1"
          } ${
            isRtl ? "left-0" : "right-0"
          } bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-gray-100 dark:border-zinc-800 py-1 min-w-[140px] z-[70]`}
        >
          {languages.map((lng) => {
            const label = i18n.getFixedT(lng)("languageName", { defaultValue: lng });
            const langCode = normalizeLanguage(lng);
            return (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`w-full px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors flex items-center gap-2.5 ${
                  currentLang === langCode ? "text-primary-400 font-semibold" : "text-gray-700 dark:text-zinc-300"
                }`}
              >
                <img
                  src={`https://flagcdn.com/w40/${getCountryCode(langCode)}.png`}
                  srcSet={`https://flagcdn.com/w80/${getCountryCode(langCode)}.png 2x`}
                  width="18"
                  className="rounded-sm object-cover shadow-sm flex-shrink-0"
                  alt={langCode}
                />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}